import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  QuestionMarkCircleIcon,
  XIcon,
} from "@heroicons/react/solid";
import { useState, useMemo, useEffect, useRef, FC } from "react";
import { orderBy } from "lodash";
import { ReactComponent as CaretDownIcon } from "assets/icons/caret-down-icon.svg";
import { useDebouncer } from "hooks/useDebouncer";
import { ANALYTICS_FILTER_API_DEBOUNCE_MS } from "utils/analytics/constants";
import { PureTooltip } from "components/Tooltip";
import Spinner from "components/Spinner";
import {
  listboxOptionClasses,
  dataSelectedListboxOptionClasses,
} from "components/Listbox";
import { cx } from "utils";

// --- Types ---

export type SelectOption = { name: string; value: string };

type BaseProps = {
  label: string;
  tooltipMessage?: string;
  options: string[] | SelectOption[];
  disabled?: boolean;
  loading?: boolean;
};

type MultiSelectProps = BaseProps & {
  multiple?: true;
  selected: string[];
  onChange: (selected: string[]) => void;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  maxSelection?: number;
  onSearch?: (term: string) => Promise<string[]>;
};

type SingleSelectProps = BaseProps & {
  multiple: false;
  value: string;
  onChange: (value: string) => void;
};

export type ComboboxSelectProps = MultiSelectProps | SingleSelectProps;

// --- Helpers ---

const normalizeOptions = (options: string[] | SelectOption[]): SelectOption[] => {
  if (options.length === 0) return [];
  if (typeof options[0] === "string") {
    return (options as string[]).map((s) => ({ name: s, value: s }));
  }
  return options as SelectOption[];
};

const isMultiMode = (props: ComboboxSelectProps): props is MultiSelectProps =>
  props.multiple !== false;

// --- Shared sub-components ---

const DropdownLabel: FC<{ label: string; tooltipMessage?: string }> = ({ label, tooltipMessage }) => (
  <label className="flex items-center gap-1 mb-1">
    {label}
    <PureTooltip content={tooltipMessage || `Filter by ${label}.`}>
      <QuestionMarkCircleIcon className="w-4 h-4 text-gray-500 cursor-pointer" />
    </PureTooltip>
  </label>
);

const inputClasses = "flex-1 w-full border-lightGray cursor-pointer shadow focus:outline-none focus:ring-1 focus:ring-indigo-500 px-2.5 py-2.5 border bg-white";
const optionsPanelClasses = "absolute w-full mt-1 rounded border bg-white shadow-lg ring-1 ring-lightGray max-h-60 overflow-auto py-1 z-50";

// --- Multi-select mode ---

const MultiMode: FC<MultiSelectProps> = ({
  label,
  tooltipMessage,
  options: rawOptions,
  selected,
  onChange,
  disabled = false,
  loading = false,
  enableSearch = true,
  searchPlaceholder = "Search...",
  maxSelection,
  onSearch,
}) => {
  const normalizedOptions = useMemo(() => normalizeOptions(rawOptions), [rawOptions]);
  const optionValues = useMemo(() => normalizedOptions.map((o) => o.value), [normalizedOptions]);

  const [query, setQuery] = useState("");
  const [backendResults, setBackendResults] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebouncer(query, ANALYTICS_FILTER_API_DEBOUNCE_MS);

  const localMatches = useMemo(() => {
    if (!query) return optionValues;
    const q = query.toLowerCase();
    return normalizedOptions
      .filter((opt) => opt.name.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q))
      .map((opt) => opt.value);
  }, [normalizedOptions, optionValues, query]);

  useEffect(() => {
    if (!onSearch || !debouncedQuery || localMatches.length > 0) {
      setBackendResults([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    onSearch(debouncedQuery)
      .then((results) => { if (!cancelled) setBackendResults(results); })
      .catch(() => { if (!cancelled) setBackendResults([]); })
      .finally(() => { if (!cancelled) setIsSearching(false); });

    return () => { cancelled = true; };
  }, [debouncedQuery, localMatches, onSearch]);

  // After selection changes while input is focused (dropdown open),
  // clear the input so the user can immediately type a new search.
  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.value = "";
    }
  }, [selected]);

  const showSearching = Boolean(onSearch) && Boolean(query) && (isSearching || query !== debouncedQuery);

  const displayOptions = useMemo(() => {
    // Always include selected items so they remain visible after search clears
    const uniqueValues = Array.from(new Set([...selected, ...localMatches, ...backendResults]));
    return orderBy(
      uniqueValues,
      [(v) => !selected.includes(v), (v) => v.toLowerCase()],
      ["asc", "asc"],
    );
  }, [localMatches, backendResults, selected]);

  const getDisplayName = (value: string): string => {
    const opt = normalizedOptions.find((o) => o.value === value);
    return opt?.name ?? value;
  };

  const handleSelect = (newSelected: string[]) => {
    // Headless UI's multi-select toggle adds the option's `value` as an item.
    // The "All" option has value={[]}, which gets appended as an array element
    // (e.g. ["domain.com", []]). Treat this as a "select all" (clear) action
    // so keyboard selection (Enter/Space) works the same as the click handler.
    if (newSelected.some((v) => Array.isArray(v))) {
      handleSelectAll();
      return;
    }
    if (maxSelection && newSelected.length > maxSelection) return;
    onChange(newSelected);
    setQuery("");
    setBackendResults([]);
  };

  const handleRemoveChip = (item: string) => {
    onChange(selected.filter((s) => s !== item));
  };

  const handleSelectAll = () => {
    onChange([]);
    setQuery("");
    setBackendResults([]);
  };

  const displayText = loading ? "Loading..." : selected.length === 0 ? "All" : `${selected.length} selected`;

  return (
    <div className="relative">
      <DropdownLabel label={label} tooltipMessage={tooltipMessage} />

      <Combobox multiple value={selected} onChange={handleSelect} disabled={disabled}>
        {(comboboxRenderProps: any) => (
          <>
            <ComboboxButton className="relative w-full flex items-center">
              <ComboboxInput
                ref={inputRef}
                readOnly={!enableSearch}
                className={cx(inputClasses, !enableSearch && "caret-transparent")}
                displayValue={() => (enableSearch && comboboxRenderProps.open) ? "" : displayText}
                onChange={enableSearch ? (e) => setQuery(e.target.value) : undefined}
                placeholder={searchPlaceholder || displayText}
              />
              <CaretDownIcon
                className={cx(
                  "w-2.5 h-2.5 text-black transition-transform absolute right-2",
                  comboboxRenderProps.open && "rotate-180",
                )}
              />
            </ComboboxButton>

            <ComboboxOptions className={optionsPanelClasses}>
              <ComboboxOption
                value={[]}
                onClick={handleSelectAll}
                className={cx(listboxOptionClasses, selected.length === 0 && "bg-brandPrimary text-white")}
              >
                <div className="flex items-center justify-between">
                  <span>All</span>
                  {selected.length === 0 && <CheckIcon className="w-4 h-4" />}
                </div>
              </ComboboxOption>

              {maxSelection && selected.length > 0 && (
                <div className="px-4 py-1.5 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                  {selected.length} of max {maxSelection} selected
                </div>
              )}

              {loading ? (
                <div className="px-4 py-2 text-gray-500 text-sm flex items-center gap-2">
                  <Spinner size="XS" theme="primary" />
                  Loading options...
                </div>
              ) : showSearching ? (
                <div className="px-4 py-2 text-gray-500 text-sm flex items-center gap-2">
                  <Spinner size="XS" theme="primary" />
                  Searching...
                </div>
              ) : displayOptions.length === 0 ? (
                <div className="px-4 py-2 text-gray-500 text-sm">
                  {query ? "No matches found" : "No options available"}
                </div>
              ) : (
                displayOptions.map((optionValue, optionIndex) => {
                  const isOptionSelected = selected.includes(optionValue);
                  const isOptionDisabled = !isOptionSelected && Boolean(maxSelection) && selected.length >= (maxSelection || 0);
                  const prev = displayOptions[optionIndex - 1];
                  const showSeparator = optionIndex > 0 && selected.includes(prev) && !isOptionSelected;

                  return (
                    <div key={optionValue}>
                      {showSeparator && <div className="border-t border-gray-200" />}
                      <ComboboxOption
                        value={optionValue}
                        disabled={isOptionDisabled}
                        className={({ active }) =>
                          cx(
                            listboxOptionClasses,
                            dataSelectedListboxOptionClasses,
                            active && "bg-brandPrimary text-white",
                            isOptionDisabled && "opacity-50 cursor-not-allowed",
                          )
                        }
                      >
                        {({ selected: isSelected }) => (
                          <div className="flex items-center justify-between">
                            <span className="truncate pr-2" title={getDisplayName(optionValue)}>
                              {getDisplayName(optionValue)}
                            </span>
                            {isSelected && <CheckIcon className="w-4 h-4" />}
                          </div>
                        )}
                      </ComboboxOption>
                    </div>
                  );
                })
              )}
            </ComboboxOptions>
          </>
        )}
      </Combobox>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.map((item) => (
            <span
              key={item}
              className="flex items-center bg-gray-100 px-2 py-1 rounded text-sm text-gray-700"
            >
              {getDisplayName(item)}
              <XIcon
                className="h-4 w-4 ml-1 cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={() => handleRemoveChip(item)}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Single-select mode ---

const SingleMode: FC<SingleSelectProps> = ({
  label,
  tooltipMessage,
  options: rawOptions,
  value,
  onChange,
  disabled = false,
}) => {
  const normalizedOptions = useMemo(() => normalizeOptions(rawOptions), [rawOptions]);

  const displayText = useMemo(() => {
    const opt = normalizedOptions.find((o) => o.value === value);
    return opt?.name ?? value;
  }, [normalizedOptions, value]);

  return (
    <div className="relative">
      <DropdownLabel label={label} tooltipMessage={tooltipMessage} />

      <Combobox value={value} onChange={onChange} disabled={disabled}>
        {(comboboxRenderProps: any) => (
          <>
            <ComboboxButton className="relative w-full flex items-center">
              <ComboboxInput
                readOnly
                className={cx(inputClasses, "caret-transparent")}
                displayValue={() => displayText}
              />
              <CaretDownIcon
                className={cx(
                  "w-2.5 h-2.5 text-black transition-transform absolute right-2",
                  comboboxRenderProps.open && "rotate-180",
                )}
              />
            </ComboboxButton>

            <ComboboxOptions className={optionsPanelClasses}>
              {normalizedOptions.map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option.value}
                  className={({ active }) =>
                    cx(
                      listboxOptionClasses,
                      dataSelectedListboxOptionClasses,
                      active && "bg-brandPrimary text-white",
                    )
                  }
                >
                  {({ selected: isSelected }) => (
                    <div className="flex items-center justify-between">
                      <span className="truncate pr-2">{option.name}</span>
                      {isSelected && <CheckIcon className="w-4 h-4" />}
                    </div>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </>
        )}
      </Combobox>
    </div>
  );
};

// --- Main component ---

const ComboboxSelect: FC<ComboboxSelectProps> = (props) => {
  if (isMultiMode(props)) {
    return <MultiMode {...props} />;
  }
  return <SingleMode {...props} />;
};

export default ComboboxSelect;
